"use client";

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { Address } from "viem";

import { defaultAccountState, defaultPositions } from "#/config/accounts";
import { defaultGreeks } from "#/config/greeks";
import { useMarket } from "#/store/app/portfolio/market";
import { useAccount } from "#/store/wallet/account/account";
import {
  type AccountStateByPortfolioSymbol,
  type PositionsByPairs,
} from "#/types/accounts";
import {
  MarginAccountStatePairs,
  Pairs,
  PortfolioSymbol,
  type PortfolioSymbolType,
} from "#/types/markets";
import { getMmrImr } from "#/utils/getMmrImr";
import { transformPositions } from "#/utils/transformPositions";

import { fetchAccountState } from "../kyan.service";
import { type AccountStateResponse } from "../kyan.types";
import { defaultApiResponseHandler } from "../kyan.utils";

const transformAccountState = (
  data?: AccountStateResponse,
): AccountStateByPortfolioSymbol => {
  return Object.fromEntries(
    Object.values(PortfolioSymbol).map((portfolioSymbol) => {
      const marginAccount = data?.margin_accounts.find(
        (item) => item.pair === MarginAccountStatePairs[portfolioSymbol],
      );

      const equity = marginAccount?.equity ?? 0;
      const mm = marginAccount?.mm ?? 0;
      const im = marginAccount?.im ?? 0;

      const { imr, mmr } = getMmrImr({ equity, im, mm });

      return [
        portfolioSymbol,
        {
          pair: MarginAccountStatePairs[portfolioSymbol],
          mmr,
          imr,
          equity,
          mm,
          im,
          greeks: marginAccount?.portfolio_greeks ?? defaultGreeks,
        },
      ];
    }),
  ) as AccountStateByPortfolioSymbol;
};

const transform = (data?: AccountStateResponse) => {
  const accountState = transformAccountState(data);

  const positions = Object.fromEntries(
    Object.values(Pairs).map((pair) => {
      const marginAccount = data?.margin_accounts.find(
        (item) => item.pair === (pair as unknown as MarginAccountStatePairs),
      );
      const transformedPositions = transformPositions(
        marginAccount?.positions || [],
      );

      return [pair, transformedPositions];
    }),
  ) as PositionsByPairs;

  return { accountState, positions };
};

export const useFetchAccountState = ({
  account,
  portfolio,
  options: { enabled = true, ...rest } = {},
}: {
  account?: Address;
  portfolio?: PortfolioSymbolType;
  options?: Partial<
    UseQueryOptions<
      AccountStateResponse,
      Error,
      {
        accountState: AccountStateByPortfolioSymbol;
        positions: PositionsByPairs;
      }
    >
  >;
} = {}) => {
  const { market } = useMarket();
  const { account: currentAccount } = useAccount();

  const effectiveAccount = account || currentAccount;
  const effectivePortfolio: PortfolioSymbolType =
    portfolio ?? market.underlying;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["fetchAccountState", effectiveAccount],
    queryFn: () =>
      defaultApiResponseHandler({
        serviceName: "Fetch Account State",
        payload: effectiveAccount!,
        service: fetchAccountState,
      }),
    select: transform,
    enabled: Boolean(effectiveAccount) && enabled,
    staleTime: 10_000,
    ...rest,
  });

  const { accountState = defaultAccountState, positions = defaultPositions } =
    data || {};

  const unallocated = effectiveAccount
    ? accountState[PortfolioSymbol.USDC]
    : defaultAccountState[PortfolioSymbol.USDC];
  const portfolioState = effectiveAccount
    ? accountState[effectivePortfolio]
    : unallocated;
  const hasZeroEquity = effectiveAccount ? portfolioState.equity <= 0 : true;
  const hasTransferableEquity = effectiveAccount
    ? unallocated.equity > 0
    : false;
  const hasEquitySomewhere = effectiveAccount
    ? Object.values(accountState).some((item) => item.equity > 0)
    : false;
  const accountValue = effectiveAccount
    ? Object.values(accountState).reduce((sum, { equity }) => sum + equity, 0)
    : 0;

  return {
    isLoading,
    positions,
    accountState,
    accountValue,
    hasZeroEquity,
    portfolioState,
    hasEquitySomewhere,
    hasTransferableEquity,
    refetch,
  };
};
